
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, doc, updateDoc, serverTimestamp, Timestamp, onSnapshot, query, orderBy, where, getDocs, getDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Button, TextField, Box, CircularProgress,
    FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox,
    Stack, Divider, Paper, Typography, Autocomplete, Grid,
    Tabs, Tab, Container, Badge, Switch, Chip, IconButton, Tooltip
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import PartSelectionSection from '../components/PartSelectionSection';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AddCommentIcon from '@mui/icons-material/AddComment';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
    hizmetTipleri, aracTurleri, camParts, seramikParts, ppfParts, hizliNotEtiketleri
} from '../components/PreInterviewConstants';
import { parseNotlar, notEkle, formatNotTarihi } from '../components/PreInterviewNotes';
import { formatPlaka, capitalizeWords, checkCustomer } from '../components/PreInterviewFormUtils';

const PreInterviewForm = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const { id } = useParams(); // URL'den ID al (varsa düzenleme modu)
    const isEditing = Boolean(id);

    const [loading, setLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);

    // Araç Marka/Model Verileri
    const [brands, setBrands] = useState([]);

    // Form state - Müşteri
    const [adSoyad, setAdSoyad] = useState('');
    const [telefon, setTelefon] = useState('');
    // Notlar artık tarihli kayıt listesi (K9). Eski tek-string kayıtlar okurken diziye çevrilir.
    const [notlarListesi, setNotlarListesi] = useState([]);
    const [orijinalNotSayisi, setOrijinalNotSayisi] = useState(0);
    const [yeniNot, setYeniNot] = useState('');
    const [hatirlatmaTarihi, setHatirlatmaTarihi] = useState(null);
    // `durum` formdan elle SEÇİLMEZ (sekme/arşiv/dönüşüm akışı yazar) ama kaydedilirken
    // korunur — yazılmazsa mevcut kayıtlar 'Yeni Görüşme'ye sıfırlanır.
    const [durum, setDurum] = useState('Yeni Görüşme');

    // Form state - Araç
    const [aracPlaka, setAracPlaka] = useState('');
    const [plakaBekleniyor, setPlakaBekleniyor] = useState(false);
    const [aracTuru, setAracTuru] = useState('');
    const [aracMarka, setAracMarka] = useState('');
    const [aracModel, setAracModel] = useState('');
    const [aracYil, setAracYil] = useState('');
    const [aracRenk, setAracRenk] = useState('');
    const [teslimTarihi, setTeslimTarihi] = useState(null);

    // Müşteri kontrolü state'leri
    const [musteriId, setMusteriId] = useState(null);
    const [musteriDurumu, setMusteriDurumu] = useState('');
    const [aracId, setAracId] = useState(null);

    // Data state
    const [camFilmiData, setCamFilmiData] = useState([]);
    const [ppfData, setPpfData] = useState([]);
    const [seramikData, setSeramikData] = useState([]);
    const [paketData, setPaketData] = useState([]);
    const [digerHizmetData, setDigerHizmetData] = useState([]);
    const [activeServiceTab, setActiveServiceTab] = useState(0);
    const [toplamTutar, setToplamTutar] = useState(0);
    const [allServices, setAllServices] = useState([]);
    // const [secilenHizmetler, setSecilenHizmetler] = useState([]); // Gerekirse kullanılabilir

    // Filtered services
    const filmServices = allServices.filter(s => s.tip === 'Cam Filmi');
    const ppfServices = allServices.filter(s => s.tip === 'PPF Kaplama');
    const seramikServices = allServices.filter(s => s.tip === 'Seramik Kaplama');
    const paketServices = allServices.filter(s => s.tip === 'Paketler');
    const detayServices = allServices.filter(s => s.tip === 'Detay Temizlik');
    const yikamaServices = allServices.filter(s => s.tip === 'Yıkama');
    const gocukServices = allServices.filter(s => s.tip === 'Göçük Düzeltme');
    const digerServices = allServices.filter(s => s.tip === 'Diğer');

    // Markaları çek
    useEffect(() => {
        const q = query(collection(db, "vehicleBrands"), orderBy("name", "asc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setBrands(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, []);

    // Hizmetleri çek
    useEffect(() => {
        const q = query(collection(db, "services"), orderBy("hizmetAdi", "asc"));
        const unsubscribe = onSnapshot(q, snapshot => {
            setAllServices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, []);

    // Düzenleme modu: Verileri yükle
    useEffect(() => {
        if (isEditing && id) {
            const fetchInterview = async () => {
                try {
                    const docRef = doc(db, "preInterviews", id);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        const data = docSnap.data();

                        setAdSoyad(data.adSoyad || '');
                        setTelefon(data.telefon || '');
                        setAracPlaka(data.aracPlaka || '');
                        setAracMarka(data.aracMarka || '');
                        setAracModel(data.aracModel || '');
                        setAracYil(data.aracYil || '');
                        setAracRenk(data.aracRenk || '');
                        setAracTuru(data.aracTuru || '');
                        setTeslimTarihi(data.teslimTarihi ? data.teslimTarihi.toDate() : null);
                        const mevcutNotlar = parseNotlar(data.notlar);
                        setNotlarListesi(mevcutNotlar);
                        setOrijinalNotSayisi(mevcutNotlar.length);
                        setDurum(data.durum || 'Yeni Görüşme');
                        setHatirlatmaTarihi(data.hatirlatmaTarihi ? data.hatirlatmaTarihi.toDate() : null);
                        setPlakaBekleniyor(Boolean(data.plakaBekleniyor));
                        // setSecilenHizmetler(data.ilgilenilenHizmetler || []);

                        setCamFilmiData(data.uygulamalar?.camFilmi || []);
                        setPpfData(data.uygulamalar?.ppf || []);
                        setSeramikData(data.uygulamalar?.seramik || []);
                        setPaketData(data.uygulamalar?.paketler || []);
                        setDigerHizmetData(data.uygulamalar?.diger || []); // Diğer verisi

                        setMusteriId(data.musteriId || null);
                        setAracId(data.aracId || null);
                        setMusteriDurumu(data.musteriId ? 'mevcut' : '');
                    } else {
                        alert("Kayıt bulunamadı!");
                        navigate('/on-gorusme');
                    }
                } catch (error) {
                    console.error("Veri yükleme hatası:", error);
                    alert("Veri yüklenirken hata oluştu.");
                } finally {
                    setLoading(false);
                }
            };
            fetchInterview();
        }
    }, [isEditing, id, navigate]);

    // Toplam tutar hesaplama
    useEffect(() => {
        let total = 0;
        [camFilmiData, ppfData, seramikData, paketData].forEach(dataSet => {
            dataSet.forEach(item => {
                if (item.satisTipi === 'paket') {
                    total += (item.paketFiyat || item.fiyat || 0);
                } else {
                    total += (item.fiyat || 0);
                }
            });
        });
        digerHizmetData.forEach(item => { total += (item.fiyat || 0); });
        setToplamTutar(total);
    }, [camFilmiData, ppfData, seramikData, paketData, digerHizmetData]);

    const handleSimpleServiceChange = (service, isChecked) => {
        let newData = [...digerHizmetData];
        if (isChecked) {
            let price = service.fiyat || 0;
            if (aracTuru && service.fiyatlar && service.fiyatlar[aracTuru]) {
                price = service.fiyatlar[aracTuru];
            }
            newData.push({
                serviceId: service.id,
                hizmetAdi: service.hizmetAdi,
                tip: service.tip,
                fiyat: price
            });
        } else {
            newData = newData.filter(item => item.serviceId !== service.id);
        }
        setDigerHizmetData(newData);
    };

    const renderSimpleServiceSelection = (categoryName, services) => {
        return (
            <Box sx={{ mt: 2 }}>
                {services.length === 0 ? (
                    <Typography color="text.secondary">Bu kategoride hizmet bulunamadı.</Typography>
                ) : (
                    <Grid container spacing={2}>
                        {services.map(service => {
                            const isSelected = digerHizmetData.some(item => item.serviceId === service.id);
                            let displayPrice = service.fiyat || 0;
                            if (aracTuru && service.fiyatlar && service.fiyatlar[aracTuru]) {
                                displayPrice = service.fiyatlar[aracTuru];
                            }

                            return (
                                <Grid item xs={12} sm={6} md={4} key={service.id}>
                                    <Paper variant="outlined" sx={{
                                        p: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        '&:hover': { borderColor: 'primary.main', boxShadow: 1 }
                                    }}
                                        onClick={() => handleSimpleServiceChange(service, !isSelected)}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={(e) => { }} // onClick parent'ta handle ediliyor
                                                    size="small"
                                                />
                                            }
                                            label={service.hizmetAdi}
                                            sx={{ pointerEvents: 'none' }} // Tıklamayı parent'a bırak
                                        />
                                        <Typography variant="body2" fontWeight="bold">
                                            {displayPrice.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Box>
        );
    };

    const handleTelefonChange = (e) => {
        const newTelefon = e.target.value;
        setTelefon(newTelefon);
        if (newTelefon.length >= 10) {
            checkCustomer(newTelefon, setMusteriId, setMusteriDurumu, setAdSoyad, adSoyad);
        } else {
            setMusteriId(null);
            setMusteriDurumu('');
        }
    };

    const notYazani = currentUser?.username || currentUser?.displayName || currentUser?.email || '';

    // metin verilmezse kutudaki taslak eklenir; hızlı etiket tıklaması kutuyu TEMİZLEMEZ
    // (temizleseydi yazılmış ama henüz eklenmemiş not sessizce kaybolurdu).
    const handleNotEkle = (metin) => {
        const kutudan = metin === undefined;
        const temiz = (kutudan ? yeniNot : metin).trim();
        if (!temiz) return;
        setNotlarListesi(prev => notEkle(prev, temiz, notYazani));
        if (kutudan) setYeniNot('');
    };

    // Yalnızca bu oturumda eklenen (henüz kaydedilmemiş) notlar geri alınabilir;
    // kayıtlı notlar asla silinmez (K9).
    const handleNotGeriAl = (index) => {
        setNotlarListesi(prev => prev.filter((_, i) => i !== index));
    };

    const handlePlakaBekleniyorChange = (checked) => {
        setPlakaBekleniyor(checked);
        if (checked) setAracPlaka('');
    };

    // Geçersiz Date (elle yarım yazılmış tarih) Timestamp.fromDate ile NaN'lı Timestamp
    // üretir; hata ancak yazma anında patlar ve o ana kadar müşteri/araç dokümanı çoktan
    // oluşmuş olur. Bu yüzden tarihler ÖNCE doğrulanır.
    const gecerliTarihMi = (deger) => deger instanceof Date && !isNaN(deger.getTime());
    const tarihToTimestamp = (deger) => (gecerliTarihMi(deger) ? Timestamp.fromDate(deger) : null);

    const handleSubmit = async () => {
        if (!currentUser) { alert('Oturumunuz açık değil.'); return; }
        if (!adSoyad || !telefon) { alert('Ad Soyad ve Telefon zorunludur.'); return; }
        if (teslimTarihi && !gecerliTarihMi(teslimTarihi)) { alert('Tahmini teslim alım tarihi geçersiz.'); return; }
        if (hatirlatmaTarihi && !gecerliTarihMi(hatirlatmaTarihi)) { alert('Hatırlatma tarihi geçersiz.'); return; }

        setIsSaving(true);
        try {
            const currentSecilenHizmetler = [];
            if (camFilmiData.length > 0) currentSecilenHizmetler.push('Cam Filmi');
            if (ppfData.length > 0) currentSecilenHizmetler.push('PPF Kaplama');
            if (seramikData.length > 0) currentSecilenHizmetler.push('Seramik Kaplama');
            if (paketData.length > 0) currentSecilenHizmetler.push('Paketler');

            digerHizmetData.forEach(item => {
                if (item.tip && !currentSecilenHizmetler.includes(item.tip)) {
                    currentSecilenHizmetler.push(item.tip);
                }
            });

            // Otomatik müşteri ve araç oluşturma mantığı burada tekrar edilebilir veya backend/utils'e taşınabilir
            // Basitlik için burada kopyalıyorum ama utils'e taşısaydık daha iyi olurdu.
            let finalMusteriId = musteriId;
            if (!finalMusteriId && telefon) {
                const newCustomerRef = await addDoc(collection(db, "customers"), {
                    adSoyad, telefon, createdAt: serverTimestamp(), kaynak: 'Ön Görüşme'
                });
                finalMusteriId = newCustomerRef.id;
            }

            // Plaka HER ZAMAN string yazılır (asla null/undefined). "Plaka sonradan
            // eklenecek" işaretliyse boş string gider → otomatik araç oluşturma da
            // kendiliğinden atlanır (plakasız kayıt iş emrine dönüşemez, K4).
            const plakaFinal = plakaBekleniyor ? '' : (aracPlaka || '').toUpperCase().trim();

            let finalAracId = aracId;
            if (!finalAracId && plakaFinal && finalMusteriId) {
                const aracQuery = query(collection(db, "vehicles"), where("plaka", "==", plakaFinal));
                const aracSnapshot = await getDocs(aracQuery);
                if (!aracSnapshot.empty) {
                    finalAracId = aracSnapshot.docs[0].id;
                } else {
                    const newVehicleRef = await addDoc(collection(db, "vehicles"), {
                        plaka: plakaFinal, marka: aracMarka || '', model: aracModel || '', yil: aracYil || '', renk: aracRenk || '', tip: aracTuru || '', musteriId: finalMusteriId, createdAt: serverTimestamp()
                    });
                    finalAracId = newVehicleRef.id;
                }
            }

            // Kutuda yazılıp "Ekle"ye basılmamış not kaybolmasın.
            const bekleyenNot = yeniNot.trim();
            const notlarKaydedilecek = bekleyenNot
                ? notEkle(notlarListesi, bekleyenNot, notYazani)
                : notlarListesi;

            // DİKKAT: `durum` bilerek dataToSave'in DIŞINDA. Form bu alanı düzenlemiyor;
            // yazsaydı formun açıldığı andaki BAYAT değeri geri yazardı — form açıkken
            // başka biri "Randevu Oluştur" (durum: 'Müşteriye Dönüştü') derse kayıt
            // arşivden çıkar ve aynı ön görüşmeden ikinci randevu açılabilirdi.
            // updateDoc yazılmayan alana dokunmaz; yeni kayıtta durum aşağıda eklenir.
            const dataToSave = {
                adSoyad, telefon,
                aracPlaka: plakaFinal, plakaBekleniyor, aracMarka, aracModel, aracYil, aracRenk, aracTuru,
                teslimTarihi: tarihToTimestamp(teslimTarihi),
                notlar: notlarKaydedilecek,
                musteriId: finalMusteriId, aracId: finalAracId,
                hatirlatmaTarihi: tarihToTimestamp(hatirlatmaTarihi),
                ilgilenilenHizmetler: currentSecilenHizmetler,
                toplamTutar,
                uygulamalar: {
                    camFilmi: camFilmiData,
                    ppf: ppfData,
                    seramik: seramikData,
                    paketler: paketData,
                    diger: digerHizmetData
                }
            };

            if (isEditing) {
                // `durum` payload'da yok → mevcut değer olduğu gibi kalır.
                const docRef = doc(db, "preInterviews", id);
                await updateDoc(docRef, dataToSave);
            } else {
                await addDoc(collection(db, "preInterviews"), {
                    ...dataToSave,
                    durum: durum || 'Yeni Görüşme',
                    createdAt: serverTimestamp()
                });
            }
            navigate('/on-gorusme');
        } catch (error) {
            console.error('Hata:', error);
            alert('Bir hata oluştu: ' + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    // Sekmelerde seçim sayısı göstermek için helper
    const getSelectionCount = (index) => {
        switch (index) {
            case 0: return paketData.length;
            case 1: return camFilmiData.length;
            case 2: return ppfData.length;
            case 3: return seramikData.length;
            case 4: return digerHizmetData.filter(i => i.tip === 'Detay Temizlik').length;
            case 5: return digerHizmetData.filter(i => i.tip === 'Yıkama').length;
            case 6: return digerHizmetData.filter(i => i.tip === 'Göçük Düzeltme').length;
            case 7: return digerHizmetData.filter(i => i.tip === 'Diğer').length;
            default: return 0;
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

    return (
        <Container maxWidth={false} sx={{ mt: 2, mb: 4, px: { xs: 2, md: 4 } }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/on-gorusme')}>
                        Geri
                    </Button>
                    <Typography variant="h5" fontWeight="bold">
                        {isEditing ? 'Ön Görüşme Düzenle' : 'Yeni Ön Görüşme Formu'}
                    </Typography>
                </Stack>

                <Grid container spacing={4}>
                    {/* SOL TARA: Müşteri ve Araç Bilgileri */}
                    <Grid item xs={12} lg={4}>
                        <Stack spacing={3}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">Müşteri Bilgileri</Typography>
                                <Stack spacing={2}>
                                    <TextField
                                        label="Telefon"
                                        value={telefon}
                                        onChange={handleTelefonChange}
                                        required fullWidth
                                        placeholder="05XX XXX XX XX"
                                    />
                                    {musteriDurumu && (
                                        <Box sx={{ p: 1, borderRadius: 1, backgroundColor: musteriDurumu === 'mevcut' ? 'success.light' : 'info.light' }}>
                                            <Typography variant="caption" fontWeight="bold" color={musteriDurumu === 'mevcut' ? 'success.dark' : 'info.dark'}>
                                                {musteriDurumu === 'mevcut' ? `✓ Mevcut Müşteri: ${adSoyad}` : '+ Yeni Kayıt Olacak'}
                                            </Typography>
                                        </Box>
                                    )}
                                    <TextField
                                        label="Ad Soyad"
                                        value={adSoyad}
                                        onChange={e => setAdSoyad(e.target.value)}
                                        onBlur={e => setAdSoyad(capitalizeWords(e.target.value))}
                                        required fullWidth
                                    />
                                </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">Araç Bilgileri</Typography>
                                <Stack spacing={2}>
                                    <Box>
                                        <TextField
                                            label="Araç Plakası"
                                            value={aracPlaka}
                                            onChange={e => setAracPlaka(formatPlaka(e.target.value))}
                                            fullWidth
                                            disabled={plakaBekleniyor}
                                            placeholder="34 ABC 123"
                                            inputProps={{ maxLength: 12 }}
                                            helperText={plakaBekleniyor ? 'Plaka sonradan Düzenle ekranından girilecek.' : ' '}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={plakaBekleniyor}
                                                    onChange={e => handlePlakaBekleniyorChange(e.target.checked)}
                                                    size="small"
                                                />
                                            }
                                            label={<Typography variant="body2">Plaka sonradan eklenecek</Typography>}
                                        />
                                    </Box>
                                    <FormControl fullWidth>
                                        <InputLabel>Araç Türü (Kasa)</InputLabel>
                                        <Select value={aracTuru} label="Araç Türü (Kasa)" onChange={e => setAracTuru(e.target.value)}>
                                            {aracTurleri.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <Autocomplete
                                        freeSolo
                                        value={aracMarka}
                                        onChange={(e, newValue) => { setAracMarka(newValue || ''); setAracModel(''); }}
                                        options={brands.map(b => b.name)}
                                        renderInput={(params) => <TextField {...params} label="Marka" />}
                                    />
                                    <Autocomplete
                                        freeSolo
                                        value={aracModel}
                                        onChange={(e, newValue) => setAracModel(newValue || '')}
                                        options={brands.find(b => b.name === aracMarka)?.models || []}
                                        disabled={!aracMarka}
                                        renderInput={(params) => <TextField {...params} label="Model" />}
                                    />
                                    <Stack direction="row" spacing={2}>
                                        <TextField label="Yıl" value={aracYil} onChange={e => setAracYil(e.target.value)} fullWidth />
                                        <TextField label="Renk" value={aracRenk} onChange={e => setAracRenk(e.target.value)} fullWidth />
                                    </Stack>
                                </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">Tarihler ve Notlar</Typography>
                                <Stack spacing={2}>
                                    <DatePicker
                                        label="Tahmini Teslim Alım Tarihi"
                                        value={teslimTarihi}
                                        onChange={setTeslimTarihi}
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                    <DateTimePicker
                                        label="Hatırlatma Tarihi (opsiyonel)"
                                        value={hatirlatmaTarihi}
                                        onChange={setHatirlatmaTarihi}
                                        ampm={false}
                                        slotProps={{
                                            textField: { fullWidth: true, helperText: 'Kartta rozet olarak görünür; tarihi geçerse kırmızı uyarı verir.' },
                                            actionBar: { actions: ['clear', 'accept'] }
                                        }}
                                    />

                                    <Divider textAlign="left">
                                        <Typography variant="caption" color="text.secondary">
                                            Notlar{notlarListesi.length > 0 ? ` (${notlarListesi.length})` : ''}
                                        </Typography>
                                    </Divider>

                                    <Box>
                                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                            {hizliNotEtiketleri.map(etiket => (
                                                <Chip
                                                    key={etiket}
                                                    label={etiket}
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleNotEkle(etiket)}
                                                />
                                            ))}
                                        </Stack>
                                        <TextField
                                            label="Yeni not"
                                            multiline
                                            rows={2}
                                            value={yeniNot}
                                            onChange={e => setYeniNot(e.target.value)}
                                            fullWidth
                                            placeholder="Görüşmede konuşulanlar…"
                                        />
                                        <Button
                                            startIcon={<AddCommentIcon />}
                                            onClick={() => handleNotEkle()}
                                            disabled={!yeniNot.trim()}
                                            size="small"
                                            sx={{ mt: 1 }}
                                        >
                                            Not Ekle
                                        </Button>
                                    </Box>

                                    {notlarListesi.length === 0 ? (
                                        <Typography variant="caption" color="text.secondary">
                                            Henüz not yok. Eklenen notlar tarihiyle birlikte saklanır, eskiler silinmez.
                                        </Typography>
                                    ) : (
                                        <Stack spacing={1} sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                                            {notlarListesi.map((not, index) => ({ not, index })).reverse().map(({ not, index }) => {
                                                const kaydedilmedi = index >= orijinalNotSayisi;
                                                const tarihMetni = formatNotTarihi(not.tarih);
                                                return (
                                                    <Paper
                                                        key={`${index}-${not.metin.substring(0, 12)}`}
                                                        variant="outlined"
                                                        sx={{ p: 1.25, bgcolor: kaydedilmedi ? '#FFF8E1' : 'grey.50' }}
                                                    >
                                                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', flexGrow: 1 }}>
                                                                {not.metin}
                                                            </Typography>
                                                            {kaydedilmedi && (
                                                                <Tooltip title="Bu notu geri al (henüz kaydedilmedi)">
                                                                    <IconButton size="small" onClick={() => handleNotGeriAl(index)}>
                                                                        <DeleteOutlineIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Stack>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {kaydedilmedi
                                                                ? 'Kaydedilmedi — Kaydet ile yazılacak'
                                                                : (tarihMetni || 'Eski kayıt (tarihsiz)')}
                                                            {not.yazan ? ` · ${not.yazan}` : ''}
                                                        </Typography>
                                                    </Paper>
                                                );
                                            })}
                                        </Stack>
                                    )}
                                </Stack>
                            </Paper>
                        </Stack>
                    </Grid>

                    {/* SAĞ TARAF: Hizmet Seçimi */}
                    <Grid item xs={12} lg={8}>
                        <Paper variant="outlined" sx={{ p: 2, minHeight: '600px' }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">Hizmet Seçimi</Typography>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                                <Tabs
                                    value={activeServiceTab}
                                    onChange={(e, v) => setActiveServiceTab(v)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    allowScrollButtonsMobile
                                >
                                    {hizmetTipleri.map((hizmet, index) => (
                                        <Tab
                                            key={hizmet}
                                            label={
                                                <Badge badgeContent={getSelectionCount(index)} color="primary" sx={{ px: 1 }}>
                                                    {hizmet}
                                                </Badge>
                                            }
                                        />
                                    ))}
                                </Tabs>
                            </Box>

                            <Box sx={{ p: 1 }}>
                                {/* İçerik render logic aynı, sadece Box panel wrapper ile */}
                                {activeServiceTab === 0 && <PartSelectionSection title="Paket Seçimi" parts={["Uygulanacak Paket"]} services={paketServices} data={paketData} setData={setPaketData} vehicleType={aracTuru} />}
                                {activeServiceTab === 1 && <PartSelectionSection title="Cam Filmi Detayları" parts={camParts} services={filmServices} data={camFilmiData} setData={setCamFilmiData} vehicleType={aracTuru} category="camFilmi" />}
                                {activeServiceTab === 2 && <PartSelectionSection title="PPF Kaplama Detayları" parts={ppfParts} services={ppfServices} data={ppfData} setData={setPpfData} vehicleType={aracTuru} category="ppf" />}
                                {activeServiceTab === 3 && <PartSelectionSection title="Seramik Kaplama Detayları" parts={seramikParts} services={seramikServices} data={seramikData} setData={setSeramikData} vehicleType={aracTuru} category="seramik" />}
                                {activeServiceTab === 4 && renderSimpleServiceSelection('Detay Temizlik', detayServices)}
                                {activeServiceTab === 5 && renderSimpleServiceSelection('Yıkama', yikamaServices)}
                                {activeServiceTab === 6 && renderSimpleServiceSelection('Göçük Düzeltme', gocukServices)}
                                {activeServiceTab === 7 && renderSimpleServiceSelection('Diğer', digerServices)}
                            </Box>
                        </Paper>

                        {/* Alt Bar: Toplam ve Kaydet */}
                        <Paper elevation={4} sx={{ position: 'sticky', bottom: 10, mt: 3, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 1000, bgcolor: 'background.paper' }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Toplam Tutar (KDV hariç)</Typography>
                                <Typography variant="h4" color="primary.main" fontWeight="bold">
                                    {toplamTutar.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSubmit}
                                disabled={isSaving}
                                sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
                            >
                                {isEditing ? 'Güncelle' : 'Kaydet'}
                            </Button>
                        </Paper>
                    </Grid>
                </Grid>
            </Paper>
        </Container>
    );
};

export default PreInterviewForm;
